import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'What Is Solana?',
    tagline: 'One global state, built from the ground up for throughput.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - Solana's central design bet (throughput/latency vs. validator requirements)
//   Q1 - single global state, not sharded into separate chains
//   Q2 - who proposed Solana and in what document
export const blocks: StageBlock[] = [
    content('hero', () => {
        const colors = useColors();
        return (
            <StageSection gradientBorder eyebrow="Stage 1 of 8">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 2 }}>
                    {['High throughput', 'Parallel execution', 'Single global state'].map((label) => (
                        <Chip
                            key={label}
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{ color: colors.accent.cyan, borderColor: colors.border.accent, backgroundColor: colors.accent.cyanGlow, fontFamily: '"Orbitron", sans-serif' }}
                        />
                    ))}
                </Box>
                <Typography variant="h2" sx={{
                    fontFamily: '"Orbitron", sans-serif',
                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                    lineHeight: 1.1,
                    fontWeight: 800,
                    color: colors.text.primary,
                    mb: 2,
                }}>
                    A blockchain designed around one question: how fast can consensus go?
                </Typography>
                <Prose>
                    In 2017, Anatoly Yakovenko published a whitepaper introducing Proof of History, the idea at the
                    center of Solana's design. Where Bitcoin and Ethereum prioritize keeping validator hardware
                    accessible to as many people as possible, Solana made a different bet: push throughput and
                    latency as far as possible, accepting heavier validator hardware and bandwidth requirements in
                    exchange for thousands of transactions per second and sub-second confirmation times.
                </Prose>
            </StageSection>
        );
    }),

    question(0),

    content('single-state', () => (
        <StageSection title="One ledger, not many">
            <Prose>
                A common misconception is that Solana achieves its speed by splitting into multiple chains or
                shards. It doesn't. Solana maintains a single, global state: one ledger everyone agrees on, exactly
                like Bitcoin and Ethereum. The speed instead comes from two things covered later in this track:
                executing non-conflicting transactions in parallel, and a consensus design (Proof of History) that
                avoids much of the communication overhead normally needed to agree on transaction ordering.
            </Prose>
            <KeyPoints items={[
                <>No sharding, no separate execution chains: one global state, same as the other chains covered on this platform.</>,
                <>The performance comes from how that single state is updated, not from dividing it up.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('closing', () => (
        <StageSection title="What you'll need before the deeper Solana tracks">
            <Prose>
                SOL is the network's native currency, used to pay transaction fees and to stake toward consensus.
                Before touching any of that in practice, though, it's worth understanding the concept that
                everything else in this track builds on: on Solana, absolutely everything (a wallet, a program, a
                piece of arbitrary data) is stored the same way, as an account.
            </Prose>
        </StageSection>
    )),

    question(2),
];
